"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect } from "react";
import { Heart, Activity, Stethoscope, HeartPulse, Droplet, Syringe, Star, ChevronRight, Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Preventive Cardiology",
  icon: Heart,
  items: ["Risk Assessment", "Cholesterol Management", "Lifestyle Counseling"]
}, {
  title: "Interventional Cardiology",
  icon: Activity,
  items: ["Cardiac Catheterization", "Coronary Stenting", "Angioplasty"]
}, {
  title: "Stress Testing",
  icon: HeartPulse,
  items: ["Treadmill Stress Test", "Nuclear Stress Test", "Echocardiography"]
}, {
  title: "Echocardiography",
  icon: Stethoscope,
  items: ["Transthoracic Echo", "Transesophageal Echo", "Stress Echo"]
}, {
  title: "Heart Failure Management",
  icon: Droplet,
  items: ["Medication Optimization", "Fluid Management", "Device Therapy"]
}, {
  title: "Vascular Care",
  icon: Syringe,
  items: ["Peripheral Arterial Disease", "Varicose Vein Care", "Venous Ablation"]
}];
const testimonials = [{
  quote: "They are the best and all the staff is so congenial. My wife and I have been patients for 8 years and we love Dr. Carrillo. Thanks, Florida Premier Cardiology.",
  name: "Al K.",
  rating: 5
}, {
  quote: "Dr. Carrillo has been my cardiologist for the past year at Delray Medical Center. He is an excellent, bright doctor and is extremely patient doctor. Answering all my concerns and questions. I find him thorough, caring and extremely knowledgeable.",
  name: "Mark B.",
  rating: 5
}, {
  quote: "I found Dr. Carrillo to be very friendly, courteous and attentive. A true professional in his career, well-educated and knowledgeable.",
  name: "Ed R.",
  rating: 5
}];
function BlogPostsList() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="space-y-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-20 rounded-lg bg-slate-100 animate-pulse border border-slate-200" />)}
      </div>;
  }
  if (error || posts.length === 0) {
    return <p className="text-slate-500 text-sm py-2">
        {error ? "Unable to load blog posts." : "No articles found."}
      </p>;
  }
  const stripHtml = (html?: string) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    const text = doc.body.textContent || "";
    return text.length > 140 ? text.slice(0, 140) + "..." : text;
  };
  const getThumb = (post: any) => {
    if (post.featuredImage) return post.featuredImage;
    const content = post.content || post.excerpt || "";
    if (content) {
      const doc = new DOMParser().parseFromString(content, "text/html");
      const img = doc.querySelector("img");
      if (img && img.src) return img.src;
    }
    return "";
  };
  return <div className="space-y-3">
      {posts.map((post, i) => {
      const excerpt = stripHtml(post.excerpt || post.content);
      const thumb = getThumb(post);
      return <Link key={post.id || i} data-index={i} to={post.link || "#"} className="flex items-center gap-3 p-3 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 transition-colors duration-200 block shadow-xs">
            <div className="size-20 rounded-md bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden border border-slate-200/80">
              {thumb ? <img src={thumb} alt={post.title} className="size-full object-cover" loading="lazy" /> : <HeartPulse className="size-7 text-primary/60" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-sans font-semibold text-slate-900 text-sm md:text-base leading-snug line-clamp-1">
                {post.title}
              </p>
              <p className="font-sans text-slate-600 text-xs md:text-sm leading-snug line-clamp-2 mt-1">
                {excerpt}
              </p>
            </div>
          </Link>;
    })}
    </div>;
}
export default function IntegratedClinicalOverview() {
  useEffect(() => {
    if (!document.getElementById("font-awesome-cdn")) {
      const link = document.createElement("link");
      link.id = "font-awesome-cdn";
      link.rel = "stylesheet";
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css";
      document.head.appendChild(link);
    }
  }, []);
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: servicesRef,
    isVisible: servicesVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section id="integratedclinicaloverview" className="w-full bg-white text-slate-900">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6 lg:gap-12 items-stretch">
          <div className="contents lg:flex lg:flex-col lg:col-span-7 lg:justify-between lg:space-y-8 lg:order-none">
            <div ref={introRef} className="contents lg:block lg:space-y-5" style={{
            opacity: introVisible ? 1 : 0,
            transform: introVisible ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 400ms ease-out, transform 400ms ease-out"
          }}>
              <h2 className="order-1 font-serif text-2xl font-semibold leading-[1.15] tracking-[-0.02em] text-[#152238] sm:text-3xl lg:order-none lg:text-4xl">
                {"Meet Rodolfo Carrillo-Jimenez, MD, FACC"}
              </h2>
              <p className="order-3 lg:order-none text-base sm:text-lg text-slate-600 font-sans leading-relaxed">
                {"Personalized patient-first care is at the heart of everything we do. Dr. Carrillo-Jimenez and our dedicated team deliver world-class cardiovascular expertise across state-of-the-art facilities in Delray Beach and Boynton Beach. From your first visit through ongoing follow-up care, we guide you every step of your heart health journey."}
              </p>
              <div className="order-4 lg:order-none">
                <Link to="https://www.abim.org/verify-physician.aspx?type=id&id=206394" newTab className="inline-block">
                  <Button data-fpc-board-verify variant="outline" size="sm" className="border-[#55C0ED] bg-[#F2FAFD] text-[#152238] hover:bg-[#152238] hover:text-white">
                    <Check aria-hidden="true" className="size-4 text-[#A32D2D]" />
                    Verify Board Certification
                  </Button>
                </Link>
              </div>
            </div>

            <div className="order-5 lg:order-none pt-4 border-t border-slate-200/80">
              <h3 className="text-xs font-mono font-semibold uppercase tracking-wider text-slate-500 mb-4">
                Patient Testimonials
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {testimonials.map((testimonial, i) => <div key={i} data-index={i} className="flex flex-col justify-between rounded-lg border border-slate-200/80 bg-slate-50 p-4">
                    <div className="space-y-2">
                      <div className="flex gap-1">
                        {[...Array(testimonial.rating)].map((_, j) => <Star key={j} className="size-4 fill-amber-400 text-amber-400" />)}
                      </div>
                      <p className="text-slate-600 text-xs sm:text-sm italic font-serif leading-relaxed line-clamp-5">
                        "{testimonial.quote}"
                      </p>
                    </div>
                    <div className="flex items-center gap-2 pt-3 mt-3 border-t border-slate-200/60">
                      <SafeHydrate><Avatar className="size-7 border border-slate-300">
                        <AvatarFallback className="bg-primary/15 text-primary text-xs font-bold">
                          {testimonial.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <span className="text-xs font-semibold text-slate-900">{testimonial.name}</span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          <div className="order-2 lg:order-none lg:col-span-5 flex flex-col">
            <div className="flex h-[260px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 sm:h-40 lg:h-full lg:min-h-[420px]">
              <img src="/react-current/images/site/dr-carrillo-meet-section-1.jpg" alt="Dr. Rodolfo Carrillo-Jimenez" className="w-full h-full object-cover object-top" loading="lazy" />
            </div>
          </div>
        </div>
      </div>

      <div ref={servicesRef} className="w-full border-y border-slate-200/80 bg-[#f7f8fa] py-16 sm:py-20" style={{
      opacity: servicesVisible ? 1 : 0,
      transform: servicesVisible ? "translateY(0)" : "translateY(20px)",
      transition: "opacity 400ms ease-out, transform 400ms ease-out"
    }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-8 max-w-2xl text-center">
            <h2 className="font-serif text-3xl font-semibold tracking-[-0.02em] text-[#152238] sm:text-4xl">
              {"Services"}
            </h2>
            <div className="mx-auto mt-3 h-[3px] w-10 rounded-full bg-primary" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {services.map((service, i) => {
            const Icon = service.icon;
            return <Card key={i} data-index={i} className="flex flex-col rounded-lg border border-slate-200/80 bg-white shadow-none transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">
                  <CardHeader className="pb-1 pt-3 px-5">
                    <div className="size-12 rounded-lg bg-red-50 flex items-center justify-center mb-2 text-primary shrink-0">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="font-serif text-lg font-semibold text-[#152238]">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-3 flex-1">
                    <ul className="space-y-2.5">
                      {service.items.map((item, j) => <li key={j} data-index={j} className="flex items-center gap-2.5 text-sm text-slate-600">
                          <div className="size-1.5 rounded-lg bg-primary shrink-0" />
                          <span>{item}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </div>

      <div className="w-full py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <Card className="flex h-full flex-col gap-2 rounded-lg border border-slate-200 bg-white shadow-none">
              <CardHeader className="pt-6 px-6 pb-0">
                <CardTitle className="font-serif text-xl font-semibold text-[#152238]">
                  {"New Patients"}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4 px-6 pb-6 flex-1">
                <p className="text-sm text-slate-600 leading-relaxed">
                  {"Starting treatment is easy. Fill out our New Patient Information Form before your first visit so we can prepare your personalized care plan."}
                </p>

                <div className="space-y-2">
                  {["Photo ID", "Insurance card", "Current medication list", "Recent medical records (if available)"].map((item, idx) => <div key={idx} data-index={idx} className="flex items-center gap-2">
                      <Check className="size-4 text-[#a32d2d] shrink-0" />
                      <span className="text-sm text-slate-600">{item}</span>
                    </div>)}
                </div>

                <div className="space-y-1.5 pt-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                    Payment Methods – Check or Cash Accepted
                  </p>
                  <div className="flex items-center gap-3">
                    <i className="fa-brands fa-cc-visa text-2xl text-[#1A1F71]" aria-label="Visa" />
                    <i className="fa-brands fa-cc-mastercard text-2xl text-[#EB001B]" aria-label="Mastercard" />
                    <i className="fa-brands fa-cc-amex text-2xl text-[#006FCF]" aria-label="American Express" />
                    <i className="fa-brands fa-cc-discover text-2xl text-[#F47216]" aria-label="Discover" />
                  </div>
                </div>

                <div className="pt-2">
                  <Link to="/patient-information/">
                    <Button size="lg" className="w-full sm:w-auto px-8 py-6 text-base font-medium bg-[#a32d2d] text-white hover:bg-[#7f2424]">
                      {"Patient Information"}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-[#0c1524] shadow-none md:aspect-auto">
              <Link to="/contact-us/" className="group flex flex-col h-full w-full overflow-hidden">
                <img src="/react-current/images/site/petct-machine-top-band2.jpg" alt="Siemens Biograph Trinion PET/CT scanner" className="w-full rounded-t-xl object-cover object-center aspect-[4/3] block" loading="lazy" />

                <div className="bg-[#0c1524] p-6 md:p-8 flex flex-col items-center justify-center text-center flex-1">
                  <h3 className="font-serif font-bold text-white text-2xl md:text-3xl leading-tight text-center">
                    <span>Siemens Biograph Trinion</span>
                    <br />
                    <span>PET/CT Imaging</span>
                  </h3>
                  <p className="text-gray-300 text-sm md:text-base text-center max-w-xs mt-3 md:mt-4">
                    Advanced cardiac imaging at our Boynton Beach office.
                  </p>
                  <div className="flex justify-center w-full mt-4 md:mt-6">
                    <div className="px-10 py-5 text-lg md:text-xl font-semibold bg-[#a32d2d] group-hover:bg-[#7f2424] text-white rounded-lg transition-colors shadow-md text-center">
                      Schedule Your Scan
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="flex flex-col justify-between space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-6 shadow-none md:col-span-2 lg:col-span-1">
              <div>
                <h3 className="mb-3 font-serif text-xl font-semibold text-[#152238]">
                  {"Latest from the Blog"}
                </h3>
                <PostsProvider wp_query={{
                post_type: "post",
                per_page: 3,
                orderby: "date",
                order: "desc",
                status: ["publish"]
              }}>
                  <BlogPostsList />
                </PostsProvider>
              </div>
              <div className="pt-2">
                <Link to="/blog/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
                  {"Read More Articles"}
                  <ChevronRight className="size-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
